import React, { useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition — wraps route content with spring-based enter/exit animations.
 *
 * Push (forward): slide in from the right with spring overshoot
 * Pop  (back):    slide out to the right with reverse spring
 * Yap Detail:     scale-up + fade (expanding card feel)
 *
 * Spring config: { damping: 18, stiffness: 180, mass: 0.8 }
 */

// Tab paths that should NOT animate between each other (bottom nav switches)
const TAB_PATHS = ['/discover', '/yaptab', '/explore', '/chat', '/profile', '/countries'];

function isTabPath(path: string): boolean {
  return TAB_PATHS.some(t => path === t || path.startsWith(t + '/'));
}

// Paths that use the scale-up expand animation
function isExpandPath(path: string): boolean {
  return path.startsWith('/yap/') || path.startsWith('/shout/');
}

type Direction = 'forward' | 'back' | 'tab';

export function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const historyStack = useRef<string[]>([]);
  const directionRef = useRef<Direction>('forward');

  useEffect(() => {
    const stack = historyStack.current;
    const current = location.pathname;

    // Detect if this is a "back" navigation:
    // If the new path matches the second-to-last in our stack, treat it as back
    if (stack.length >= 2 && stack[stack.length - 2] === current) {
      stack.pop();
      directionRef.current = 'back';
    } else if (stack.length > 0 && stack[stack.length - 1] === current) {
      // Same path (e.g. replace) — no animation
      directionRef.current = 'tab';
    } else {
      // Check tab-to-tab navigation
      const prev = stack.length > 0 ? stack[stack.length - 1] : '';
      if (isTabPath(prev) && isTabPath(current)) {
        directionRef.current = 'tab';
      } else {
        directionRef.current = 'forward';
      }
      stack.push(current);
    }

    // Prevent unbounded growth
    if (stack.length > 50) {
      historyStack.current = stack.slice(-30);
    }
  }, [location.pathname]);

  const direction = directionRef.current;
  const expand = isExpandPath(location.pathname);

  // Tab switches: simple crossfade (no slide)
  if (direction === 'tab') {
    return (
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    );
  }

  // Yap/Shout Detail: scale-up expand animation
  if (expand && direction === 'forward') {
    return (
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{
          type: 'spring',
          damping: 22,
          stiffness: 200,
          mass: 0.8,
        }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    );
  }

  // Standard push / pop with directional slide
  const slideDistance = 60; // px — subtle, not a full-screen slide
  const isBack = direction === 'back';

  return (
    <motion.div
      key={location.pathname}
      initial={{
        opacity: 0,
        x: isBack ? -slideDistance : slideDistance,
      }}
      animate={{
        opacity: 1,
        x: 0,
      }}
      exit={{
        opacity: 0,
        x: isBack ? slideDistance : -slideDistance,
      }}
      transition={{
        type: 'spring',
        damping: 18,
        stiffness: 180,
        mass: 0.8,
      }}
      className="h-full w-full"
    >
      {children}
    </motion.div>
  );
}
